import { PrimaryValueIcon } from '@/components/PrimaryValueIcon';
import { ImageFromServer } from '@/components/images/ImageFromServer';
import { CompletionDataExecutionTypeEnum, ExerciseConfigExecutionTypeEnum, TrainingHistoryEntry } from '@/generated';
import { useAllExercisesQuery, useDeckExercisesQuery } from '@/hooks/query-hooks';
import { FitnessCenter } from '@mui/icons-material';
import { Box, Grid, Paper, Stack, Typography } from '@mui/material';
import { BarCustomLayerProps, BarTooltipProps, ResponsiveBar } from '@nivo/bar';

export type ExerciseStatsMode = CompletionDataExecutionTypeEnum | 'total';

type DivergingExerciseDatum = {
  id: string;
  exerciseId: string;
  limb: 'L' | 'R' | '';
  value: number;
  volume: number;
  executionType: ExerciseConfigExecutionTypeEnum;
  actualValue: number;
  actualVolume: number;
  isSpacer: 0 | 1;
};

const valueLabels: Record<ExerciseStatsMode, string> = {
  [CompletionDataExecutionTypeEnum.Reps]: 'Reps',
  [CompletionDataExecutionTypeEnum.Duration]: 'Duration',
  [CompletionDataExecutionTypeEnum.Intervals]: 'Intervals',
  total: 'Total',
};

const DivergingExerciseTooltip = (props: BarTooltipProps<DivergingExerciseDatum>) => {
  const { actualValue, actualVolume, exerciseId, executionType } = props.data;
  const { exercises } = useAllExercisesQuery(true);
  const exercise = exercises?.find((item) => item.id === exerciseId);

  return (
    <Paper elevation={6} sx={{ width: 350, p: 1.25 }}>
      <Grid container columnSpacing={2} alignItems="center">
        <Grid size={5}>
          {exercise && <ImageFromServer sx={{ width: '100%', maxWidth: 100, maxHeight: 100, objectFit: 'contain' }} thumbnail alt="" id={exercise.imageId} />}
        </Grid>
        <Grid size={7} sx={{ minWidth: 0 }}>
          <Stack gap={0.75}>
            {exercise && (
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {exercise.name}
              </Typography>
            )}
            {props.id === 'volume' ? (
              <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', whiteSpace: 'nowrap', '& .MuiSvgIcon-root': { fontSize: 16 } }}>
                <PrimaryValueIcon executionType={executionType} />
                <Box component="span">×</Box>
                <FitnessCenter />
                <Box component="span">= {actualVolume}</Box>
              </Stack>
            ) : (
              <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', whiteSpace: 'nowrap', '& .MuiSvgIcon-root': { fontSize: 16 } }}>
                <PrimaryValueIcon executionType={executionType} />
                <Box component="span">= {actualValue}</Box>
              </Stack>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

export const DivergingExerciseStats = (props: { selectedEntry?: TrainingHistoryEntry; mode: ExerciseStatsMode }) => {
  const { exercises: exerciseList } = useDeckExercisesQuery(props.selectedEntry?.deckId);
  const completedExercises = props.selectedEntry?.completedTraining?.rounds.flatMap((round) => round.exercises) ?? [];
  const aggregatedByExercise = new Map<
    string,
    {
      primaryValue: number;
      secondaryValue: number;
      primaryVolume: number;
      secondaryVolume: number;
      hasSecondaryLimb: boolean;
    }
  >();

  completedExercises
    .filter((exercise) => props.mode === 'total' || exercise?.completionData.executionType === props.mode)
    .forEach((exercise) => {
      if (!exercise) return;

      const completedIntervals = exercise.completionData.completedIntervals ?? [];
      const isIntervalMode = exercise.completionData.executionType === CompletionDataExecutionTypeEnum.Intervals;
      const primaryValue = isIntervalMode ? completedIntervals.reduce((sum, interval) => sum + interval.completedPrimaryTime, 0) : (exercise.completionData.completedPrimaryValue ?? 0);
      const secondaryValue = isIntervalMode
        ? completedIntervals.reduce((sum, interval) => sum + (interval.completedSecondaryTime ?? 0), 0)
        : (exercise.completionData.completedSecondaryValue ?? 0);
      const hasSecondaryValue = isIntervalMode
        ? completedIntervals.some((interval) => interval.completedSecondaryTime !== undefined)
        : exercise.completionData.completedSecondaryValue !== undefined;
      const hasSecondaryLimb = hasSecondaryValue || exercise.completionData.completedSecondaryWeight !== undefined;
      const current = aggregatedByExercise.get(exercise.exerciseId) ?? {
        primaryValue: 0,
        secondaryValue: 0,
        primaryVolume: 0,
        secondaryVolume: 0,
        hasSecondaryLimb: false,
      };

      aggregatedByExercise.set(exercise.exerciseId, {
        primaryValue: current.primaryValue + primaryValue,
        secondaryValue: current.secondaryValue + secondaryValue,
        primaryVolume: current.primaryVolume + primaryValue * (exercise.completionData.completedPrimaryWeight ?? 0),
        secondaryVolume: current.secondaryVolume + secondaryValue * (exercise.completionData.completedSecondaryWeight ?? 0),
        hasSecondaryLimb: current.hasSecondaryLimb || hasSecondaryLimb,
      });
    });

  const data = Array.from(aggregatedByExercise.entries()).flatMap(([exerciseId, aggregated]) => {
    const isUnilateral = exerciseList?.find((exercise) => exercise.id === exerciseId)?.isUniLateral ?? false;
    const executionType = exerciseList?.find((exercise) => exercise.id === exerciseId)?.config.executionType ?? ExerciseConfigExecutionTypeEnum.Reps;
    const limbData: DivergingExerciseDatum[] = [
      {
        id: `${exerciseId}-first`,
        exerciseId,
        executionType,
        limb: isUnilateral ? 'L' : '',
        value: aggregated.primaryValue,
        volume: -aggregated.primaryVolume,
        actualValue: aggregated.primaryValue,
        actualVolume: aggregated.primaryVolume,
        isSpacer: 0,
      },
    ];

    if (isUnilateral && aggregated.hasSecondaryLimb) {
      limbData.push({
        id: `${exerciseId}-second`,
        exerciseId,
        executionType,
        limb: 'R',
        value: aggregated.secondaryValue,
        volume: -aggregated.secondaryVolume,
        actualValue: aggregated.secondaryValue,
        actualVolume: aggregated.secondaryVolume,
        isSpacer: 0,
      });
    }

    return limbData;
  });
  const maximumValue = Math.max(1, ...data.map((datum) => datum.actualValue));
  const maximumVolume = Math.max(1, ...data.map((datum) => datum.actualVolume));
  const normalizedData = data.map((datum) => ({
    ...datum,
    value: datum.actualValue / maximumValue,
    volume: -(datum.actualVolume / maximumVolume),
  }));

  const chartData: DivergingExerciseDatum[] = normalizedData.flatMap((datum, index): DivergingExerciseDatum[] => {
    const nextDatum = normalizedData[index + 1];
    const isLastLimbOfExercise = !nextDatum || nextDatum.exerciseId !== datum.exerciseId;
    const executionType = exerciseList?.find((exercise) => exercise.id === datum.exerciseId)?.config.executionType ?? ExerciseConfigExecutionTypeEnum.Reps;

    if (!isLastLimbOfExercise || index === data.length - 1) return [datum];

    return [
      datum,
      {
        id: `${datum.exerciseId}-spacer`,
        exerciseId: '',
        executionType,
        limb: '' as const,
        value: 0,
        volume: 0,
        actualValue: 0,
        actualVolume: 0,
        isSpacer: 1 as const,
      },
    ];
  });
  const displayedData = chartData;
  const axisTickValues = [-1, -0.5, 0, 0.5, 1];
  const axisSectionLabels = (layerProps: BarCustomLayerProps<DivergingExerciseDatum>) => (
    <g aria-hidden="true">
      {chartData
        .filter((datum) => !datum.isSpacer)
        .map((datum) => {
          const xScale = layerProps.xScale as any;
          const x = xScale(datum.id) + xScale.bandwidth() / 2;

          return <line key={datum.id} x1={x} x2={x} y1={0} y2={layerProps.innerHeight} stroke="rgba(255,255,255,0.35)" strokeWidth={1} />;
        })}
      <text fill="white" fontSize={12} textAnchor="middle" transform={`translate(-54, ${layerProps.innerHeight * 0.25}) rotate(-90)`}>
        {valueLabels[props.mode]}
      </text>
      <text fill="white" fontSize={12} textAnchor="middle" transform={`translate(-54, ${layerProps.innerHeight * 0.75}) rotate(-90)`}>
        Volume
      </text>
    </g>
  );

  return (
    <Paper variant="outlined" sx={{ m: 0, p: 1, flex: 1, minWidth: 0, position: 'relative' }}>
      <Stack direction="row" sx={{ width: '100%', minWidth: 0, alignItems: 'stretch' }}>
        <Stack sx={{ width: { xs: 70, sm: 90 }, flexShrink: 0, justifyContent: 'center', gap: 1, pl: 0.5 }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, flexShrink: 0, backgroundColor: '#66c2a5' }} />
            <Typography variant="caption">{valueLabels[props.mode]}</Typography>
          </Stack>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, flexShrink: 0, backgroundColor: '#fc8d62' }} />
            <Typography variant="caption">Volume</Typography>
          </Stack>
        </Stack>
        <Box sx={{ height: { xs: 340, sm: 300 }, flex: 1, minWidth: 0 }}>
        <ResponsiveBar
          data={displayedData}
          keys={['value', 'volume']}
          indexBy="id"
          groupMode="stacked"
          padding={0.05}
          valueScale={{ type: 'linear', min: -1, max: 1 }}
          indexScale={{ type: 'band', round: true }}
          colors={['#66c2a5', '#fc8d62']}
          theme={{ text: { fill: 'white' }, grid: { line: { stroke: 'rgba(255,255,255,0.18)' } } }}
          tooltip={(tooltipProps) => <DivergingExerciseTooltip {...tooltipProps} />}
          label={(datum) => String(datum.id === 'volume' ? datum.data.actualVolume : datum.data.actualValue)}
          labelSkipWidth={12}
          labelSkipHeight={12}
          axisLeft={{
            tickValues: axisTickValues,
            format: (value) => (Number(value) < 0 ? Math.abs(Number(value)) * maximumVolume : Number(value) * maximumValue),
          }}
          axisBottom={{
            tickValues: chartData.filter((datum) => !datum.isSpacer).map((datum) => datum.id),
            tickSize: 18,
            tickPadding: 6,
            tickRotation: -45,
            format: (id) => {
              const datum = displayedData.find((item) => item.id === id);
              if (datum?.isSpacer) return '';
              const exercise = exerciseList?.find((item) => item.id === datum?.exerciseId);
              const label = `${exercise?.name ?? ''}${datum?.limb ? ` – ${datum.limb}` : ''}`;
              return label.length > 18 ? `${label.slice(0, 17)}…` : label;
            },
          }}
          gridYValues={[0]}
          markers={[
            {
              axis: 'y',
              value: 0,
              lineStyle: { stroke: '#ffffff', strokeWidth: 1 },
            },
          ]}
          layers={['grid', 'axes', axisSectionLabels, 'bars', 'markers', 'legends']}
          margin={{ top: 30, right: 20, bottom: 110, left: 80 }}
        />
        </Box>
      </Stack>
      {props.selectedEntry?.bodyweight !== undefined && (
        <Typography variant="body2" sx={{ position: 'absolute', bottom: 8, left: 12 }}>
          Bodyweight: {props.selectedEntry.bodyweight} kg/lb
        </Typography>
      )}
    </Paper>
  );
};
